import type { ListView } from "@/services/list-view";

/**
 * Enriches a ListView returned from the create API (which omits can_user_edit and
 * may omit created_by) so that Save/Rename/Delete buttons are properly enabled.
 * The creator is always the owner, so can_user_edit is true.
 */
export function enrichViewFromCreate<T extends Partial<ListView>>(
    view: T,
    currentUser?: { id?: number; first_name?: string; last_name?: string; username?: string } | null
): T & Pick<ListView, "can_user_edit" | "created_by"> {
    return {
        ...view,
        can_user_edit: true,
        created_by: view.created_by ?? {
            id: currentUser?.id ?? 0,
            username: (currentUser as { username?: string })?.username ?? "",
            first_name: currentUser?.first_name ?? "",
            last_name: currentUser?.last_name ?? ""
        }
    } as T & Pick<ListView, "can_user_edit" | "created_by">;
}

/**
 * Merges API update response (limited fields) with existing view to preserve
 * can_user_edit and created_by.
 */
export function mergeViewUpdate(existing: ListView, updated: Partial<ListView>): ListView {
    return {
        ...existing,
        ...updated,
        can_user_edit: updated.can_user_edit ?? existing.can_user_edit ?? true,
        created_by: updated.created_by ?? existing.created_by
    };
}

/**
 * Logic for Save/Rename/Share buttons: disabled when !view?.id || !view?.can_user_edit
 */
export function areViewActionButtonsEnabled(view: ListView | null): boolean {
    return !!(view?.id && view?.can_user_edit);
}

/**
 * Logic for Delete button: disabled when !view?.id || (created_by && created_by.id !== userId)
 */
export function isDeleteButtonEnabled(view: ListView | null, userId?: number): boolean {
    if (!view?.id) return false;
    if (!view.created_by) return true;
    return view.created_by.id === userId;
}
